import { ReactElement } from 'react';
import { makeStyles } from '@mui/styles';
import { FormattedMessage, unsafeTranslationKey } from 'intl';

import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
} from '@mui/material';
import { WithInputForm } from 'components/dataCreationForms/withInputForm';
import { ReportForm } from 'components/report/Form';
import { ReportWizardLanguageSelector } from 'components/report/Form/ReportWizardLanguageSelector';
import {
  handleBaseReportFormValidation as defaultHandleFormValidation,
  validateBaseReport as defaultValidate,
} from 'components/dataCreationForms/report/reportValidation';
import { useFormCleanupOnUnmount } from 'presentation/dialogForms/dialogFormsHooks';
import { BaseReportContext } from 'components/dataCreationForms/report/types/baseReportContext';
import { HashMap } from 'shared/types/commonView';

const useStyles = makeStyles({
  root: {
    '& .MuiDialog-paper': {
      margin: 16,
      width: '100%',
      maxWidth: 720,
      '@media (min-width:600px)': {
        maxWidth: 920,
      },
      '@media (min-width:900px)': {
        maxWidth: 1040,
      },
    },
  },
  dialogContent: {
    padding: '24px',
    scrollbarGutter: 'stable',
  },
  dialogTitle: {
    '&.MuiDialogTitle-root': {
      padding: '24px 24px 16px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: '16px',
      // Reserve the same scrollbar gutter as the scrollable content below so
      // the language selector's right edge lines up with the form inputs.
      overflow: 'hidden',
      scrollbarGutter: 'stable',
    },
  },
});

interface CreateReportDialogProps<
  T extends HashMap<any>,
  K extends HashMap<any> = T,
> {
  useReportContext: () => BaseReportContext<T, K>;
  titleId?: string;
}

export function CreateReportDialog<
  T extends HashMap<any>,
  K extends HashMap<any> = T,
>({
  useReportContext,
  titleId = 'issue_list_send_report_action',
}: CreateReportDialogProps<T, K>): ReactElement {
  const {
    open,
    closeDialog,
    submitForm,
    isPosting,
    initialValues,
    releaseSubmitEvent,
    SUBMIT_EVENT_TYPE,
    handleFormValidation,
    validate,
    ContentSelection,
    issueParams,
    reportEstimateKind,
    wrapForm,
  } = useReportContext();
  const classes = useStyles();
  const formName = 'report';
  useFormCleanupOnUnmount(formName);

  const form = (
    <ReportForm
      isPosting={isPosting}
      onCancel={closeDialog}
      onSubmit={releaseSubmitEvent}
      ContentSelection={ContentSelection}
      issueParams={issueParams}
      reportType={reportEstimateKind}
    />
  );

  return (
    <Dialog
      className={classes.root}
      open={open}
      keepMounted={false}
      onClose={closeDialog}
      data-qa='report_create_dialog'
    >
      <WithInputForm
        values={initialValues}
        errors={{}}
        handleFormValidation={
          handleFormValidation || defaultHandleFormValidation
        }
        handleFieldValidation={validate || defaultValidate}
        onSubmit={submitForm}
        eventType={SUBMIT_EVENT_TYPE}
        dispatchSubmit={releaseSubmitEvent}
        formName={formName}
      >
        <DialogTitle
          className={classes.dialogTitle}
          data-qa='report_create_dialog_title'
        >
          <span>
            <FormattedMessage id={unsafeTranslationKey(titleId)} />
          </span>
          <ReportWizardLanguageSelector />
        </DialogTitle>
        <Divider />
        <DialogContent
          className={classes.dialogContent}
          data-qa='report_create_dialog_content'
        >
          {wrapForm ? wrapForm(form) : form}
        </DialogContent>
      </WithInputForm>
    </Dialog>
  );
}
